import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { LogOut, Home, Mail, Settings, PlusCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [properties, setProperties] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [propsRes, contactsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/properties`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/contacts`, { headers }),
      ]);

      setProperties(propsRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      toast.error('Erreur de chargement des données');
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
    toast.success('Déconnexion réussie');
  };

  const handleDeleteProperty = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette propriété ?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${BACKEND_URL}/api/properties/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Propriété supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(`${BACKEND_URL}/api/admin/contacts/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Marqué comme lu');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img
              src="https://customer-assets.emergentagent.com/job_netanya-elite-homes/artifacts/wuykmx8d_image0.png"
              alt="BuyIt.il"
              className="h-12"
            />
            <h1 className="text-xl font-bold text-[#1a365d]">Panel Administrateur</h1>
          </div>
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/settings')}
              className="flex items-center space-x-2"
            >
              <Settings className="w-4 h-4" />
              <span>Paramètres</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="flex items-center space-x-2"
            >
              <Home className="w-4 h-4" />
              <span>Voir le site</span>
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="flex items-center space-x-2 text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4" />
              <span>Déconnexion</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <Tabs defaultValue="properties" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 lg:w-1/2">
            <TabsTrigger value="properties">
              Propriétés ({properties.length})
            </TabsTrigger>
            <TabsTrigger value="contacts">
              Messages ({contacts.filter(c => !c.read).length})
            </TabsTrigger>
          </TabsList>

          {/* Properties Tab */}
          <TabsContent value="properties">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-[#1a365d]">Gestion des Propriétés</h2>
                <Button
                  onClick={() => navigate('/admin/property/new')}
                  className="bg-[#1a365d] hover:bg-[#d4af37] flex items-center space-x-2"
                >
                  <PlusCircle className="w-4 h-4" />
                  <span>Nouvelle Propriété</span>
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {properties.map((property) => (
                  <Card key={property.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <img
                      src={property.images[0] || '/placeholder.jpg'}
                      alt={property.title_en}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-4">
                      <h3 className="font-bold text-lg mb-2">{property.title_en}</h3>
                      <p className="text-sm text-gray-600 mb-2">{property.city}</p>
                      <p className="text-xl font-bold text-[#1a365d] mb-4">
                        ₪{property.price.toLocaleString()}
                      </p>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/admin/property/${property.id}`)}
                          className="flex-1"
                        >
                          Modifier
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteProperty(property.id)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {properties.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <p>Aucune propriété. Créez-en une nouvelle !</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <Card className="p-6">
              <h2 className="text-2xl font-bold text-[#1a365d] mb-6">Messages de Contact</h2>

              <div className="space-y-4">
                {contacts.map((contact) => (
                  <Card
                    key={contact.id}
                    className={`p-4 ${contact.read ? 'bg-gray-50' : 'bg-blue-50 border-l-4 border-blue-500'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="font-bold text-lg">{contact.name}</h3>
                          {!contact.read && (
                            <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded">
                              Nouveau
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-1">
                          <Mail className="w-4 h-4 inline mr-2" />
                          {contact.email} | {contact.phone}
                        </p>
                        <p className="mt-3 text-gray-700">{contact.message}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          {new Date(contact.created_at).toLocaleString('fr-FR')}
                        </p>
                      </div>
                      {!contact.read && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMarkAsRead(contact.id)}
                        >
                          Marquer comme lu
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>

              {contacts.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <p>Aucun message</p>
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
